import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useSettleCreditNote,
  getListAccountCreditNotesQueryKey,
  type CreditNote,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Upload } from "lucide-react";
import { uploadPaymentProof } from "@/lib/upload";
import { useToast } from "@/hooks/use-toast";

interface Props {
  accountId: number;
  creditNote: CreditNote | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Marks a credit note as Refunded — captures the refund payment details
// for the audit trail. Backend writes settlementDate / paymentMode /
// proofUrl / notes onto the credit_notes row.
export function MarkRefundedDialog({ accountId, creditNote, open, onOpenChange }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const settleMutation = useSettleCreditNote();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [proofUrl, setProofUrl] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) {
      setDate(new Date().toISOString().slice(0, 10));
      setPaymentMode("UPI");
      setProofUrl(null);
      setNotes("");
      setUploading(false);
    }
  }, [open]);

  async function onProof(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadPaymentProof(file);
      if (r.ok) {
        setProofUrl(r.url);
        toast({ title: "Refund proof uploaded." });
      } else {
        toast({
          title: "Couldn't upload proof",
          description: r.message || `Upload failed (HTTP ${r.status}).`,
          variant: "destructive",
        });
      }
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    if (!creditNote) return;
    if (!date) {
      toast({ title: "Pick a refund date.", variant: "destructive" });
      return;
    }
    try {
      await settleMutation.mutateAsync({
        id: creditNote.id,
        data: {
          settlementStatus: "Refunded",
          settlementDate: date,
          settlementPaymentMode: paymentMode,
          settlementProofUrl: proofUrl,
          settlementNotes: notes.trim() || null,
        },
      });
      queryClient.invalidateQueries({ queryKey: getListAccountCreditNotesQueryKey(accountId) });
      toast({ title: "Marked Refunded." });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Couldn't mark refunded.",
        description: (err as Error)?.message ?? undefined,
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mark Credit Note Refunded</DialogTitle>
          <DialogDescription>
            {creditNote
              ? `${creditNote.creditNoteNumber} · ₹${creditNote.totalReversed.toLocaleString("en-IN")}`
              : "Record the refund payment."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">
                Refund date <span className="text-destructive">*</span>
              </Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Payment mode</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Bank_Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Card">Card</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Refund proof (optional)</Label>
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm" disabled={uploading}>
                <label className="cursor-pointer">
                  {uploading ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-1.5 h-4 w-4" />
                  )}
                  {proofUrl ? "Replace" : "Upload"}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => onProof(e.target.files?.[0])}
                  />
                </label>
              </Button>
              {proofUrl && (
                <a
                  href={proofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary underline"
                >
                  View uploaded proof
                </a>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. UPI ref 6291XXXX1234, refunded against early-exit settlement"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={settleMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={save} disabled={settleMutation.isPending}>
            {settleMutation.isPending ? "Saving…" : "Mark Refunded"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
